import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import {
  getPagePath,
  getPageSlug,
  resolvePageKeyFromSlug,
  type Lang,
  getUsefulRootSlug,
  resolveHubIdFromSlug,
  resolveArticleIdFromSlug,
  buildUsefulArticlePath,
  buildUsefulHubPath,
  getUsefulRootPath
} from '@/lib/localizedRoutes';
import { cn } from '@/lib/utils';
import { Footer } from '@/components/layout/footer';
import { Home } from '@/pages/Home';
import { Catalog } from '@/pages/Catalog';
import { Useful } from '@/pages/Useful';
import { Portfolio } from '@/pages/Portfolio';
import { News } from '@/pages/News';
import { About } from '@/pages/About';
import { ESG } from '@/pages/ESG';
import { WhereToBuy } from '@/pages/WhereToBuy';
import { BecomeDealer } from '@/pages/BecomeDealer';

const VALID_LANGS = ['ru', 'en', 'uz'];

/** Returns the lang prefix from pathname (e.g. '/ru/catalog' → 'ru'), or '' */
function getLangFromPath(pathname: string): string {
  const seg = pathname.split('/')[1];
  return VALID_LANGS.includes(seg) ? seg : '';
}

/** Returns the path without the leading lang segment */
function stripLangPrefix(pathname: string): string {
  const parts = pathname.split('/');
  if (VALID_LANGS.includes(parts[1])) {
    return '/' + parts.slice(2).join('/');
  }
  return pathname;
}

// Slugs relative to lang base ('' = home, 'catalog' = /ru/catalog, etc.)
const PAGE_DEFS = [
  { slug: '', label: 'nav.home', component: Home },
  { slug: 'catalog', label: 'nav.catalog', component: Catalog },
  { slug: 'useful', label: 'nav.useful', component: Useful },
  { slug: 'portfolio', label: 'nav.portfolio', component: Portfolio },
  { slug: 'news', label: 'nav.news', component: News },
  { slug: 'about', label: 'nav.about', component: About },
  { slug: 'esg', label: 'nav.esg', component: ESG },
  { slug: 'where-to-buy', label: 'nav.whereToBuy', component: WhereToBuy },
  { slug: 'become-dealer', label: 'nav.becomeDealer', component: BecomeDealer },
];

interface MobileTabNavigationProps {
  children: React.ReactNode;
}

export const MobileTabNavigation: React.FC<MobileTabNavigationProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Current lang prefix and path-without-lang
  const langPrefix = getLangFromPath(location.pathname);
  const pathWithoutLang = stripLangPrefix(location.pathname);

  // Build full path for a slug given current lang (or an override lang)
  const buildPath = useCallback((slug: string, lang?: string) => {
    const targetLang = (lang ?? language) as Lang;
    if (slug === '') return `/${targetLang}`;
    return getPagePath(slug, targetLang);
  }, [language]);

  const getIndexFromPath = useCallback((pathname: string) => {
    const pwl = stripLangPrefix(pathname).replace(/^\//, '');
    if (pwl === '') return 0;
    if (pwl === 'catalog') return 1;
    // Catalog category pages (e.g. catalog/spc-laminate) also map to tab "Catalog".
    if (pwl.startsWith('catalog/')) return 1;

    const pageKey = resolvePageKeyFromSlug(pwl, language as Lang);
    if (!pageKey) return 0;

    const index = PAGE_DEFS.findIndex(page => page.slug === pageKey);
    return index === -1 ? 0 : index;
  }, [language]);

  const [currentIndex, setCurrentIndex] = useState(() => getIndexFromPath(location.pathname));

  const trackRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isInternalNavRef = useRef(false);
  const isScrollingProgrammaticallyRef = useRef(false);
  const currentIndexRef = useRef(currentIndex);
  const rafIdRef = useRef(0);
  const scrollEndTimerRef = useRef<number>(0);
  const scrollProgrammaticTimerRef = useRef<number>(0);

  // Touch gesture refs (no React state during gesture — avoids re-renders)
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const directionLockedRef = useRef<'h' | 'v' | null>(null);
  const pathnameRef = useRef(location.pathname);

  // Keep refs in sync with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const isMainPage = (() => {
    const segments = location.pathname.split('/').filter(Boolean).slice(1);
    if (segments.length === 0) return true; // home

    const firstSlug = segments[0];
    const pageKey = resolvePageKeyFromSlug(firstSlug, language as Lang);

    // Main pages are catalog and top-level informational pages (about, news, useful root, etc.)
    if (pageKey !== null && segments.length === 1) return true;
    if (firstSlug === 'catalog' && segments.length === 1) return true;

    // Catalog category pages (e.g. /catalog/spc-laminate) — the user expects
    // the Telegram-like swipe bar to STAY active when they navigate between
    // the 4 catalog series, and especially when they come back from a product
    // page. Without this, the back-from-product navigation leaves the user
    // stranded on a non-main route and the bottom tab swipe disappears.
    const KNOWN_CATALOG_CATEGORIES = ['spc-laminate', 'spc-panels', 'carpet-tile', 'accessories'];
    if (firstSlug === 'catalog' && segments.length === 2 && KNOWN_CATALOG_CATEGORIES.includes(segments[1])) {
      return true;
    }

    return false;
  })();

  // Reset all neighbor scrollTops to 0 (non-reactive, direct DOM)
  const resetNeighborScrollTops = useCallback((idx: number) => {
    for (let i = 0; i < PAGE_DEFS.length; i++) {
      if (i !== idx && pageRefs.current[i]) {
        pageRefs.current[i]!.scrollTop = 0;
      }
    }
  }, []);

  // Sync index from external URL change (e.g. header link click)
  useEffect(() => {
    if (!isMainPage) return;
    const index = getIndexFromPath(location.pathname);
    if (index !== currentIndexRef.current && !isInternalNavRef.current) {
      currentIndexRef.current = index;
      setCurrentIndex(index);
      resetNeighborScrollTops(index);
      if (trackRef.current) {
        isScrollingProgrammaticallyRef.current = true;
        trackRef.current.scrollTo({ left: index * window.innerWidth, behavior: 'smooth' });

        window.clearTimeout(scrollProgrammaticTimerRef.current);
        scrollProgrammaticTimerRef.current = window.setTimeout(() => {
          isScrollingProgrammaticallyRef.current = false;
        }, 500);
      }
    }
  }, [location.pathname, getIndexFromPath, isMainPage, resetNeighborScrollTops]);

  // Detect snap end via scroll event + debounce (scrollend not widely supported)
  const handleScroll = useCallback(() => {
    if (!trackRef.current || !isMainPage || isScrollingProgrammaticallyRef.current) return;

    const scrollLeft = trackRef.current.scrollLeft;
    const w = window.innerWidth;
    const nearestIdx = Math.round(scrollLeft / w);

    // Fast feedback: update active tab highlighting immediately
    if (nearestIdx !== currentIndexRef.current && nearestIdx >= 0 && nearestIdx < PAGE_DEFS.length) {
      setCurrentIndex(nearestIdx);
    }

    // Debounced URL update — only update URL when scroll settles
    window.clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = window.setTimeout(() => {
      if (!trackRef.current) return;
      const finalLeft = trackRef.current.scrollLeft;
      const finalIdx = Math.round(finalLeft / window.innerWidth);

      if (finalIdx !== currentIndexRef.current && finalIdx >= 0 && finalIdx < PAGE_DEFS.length) {
        currentIndexRef.current = finalIdx;
        setCurrentIndex(finalIdx);
        resetNeighborScrollTops(finalIdx);

        // Update URL without remount
        isInternalNavRef.current = true;
        const newPath = buildPath(PAGE_DEFS[finalIdx].slug);
        if (pathnameRef.current !== newPath) {
          navigate(newPath, { replace: true });
        }
        setTimeout(() => { isInternalNavRef.current = false; }, 150);
      }
    }, 50);
  }, [isMainPage, navigate, resetNeighborScrollTops, buildPath]);

  // Attach non-passive touch listeners for gesture locking
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !isMainPage) return;

    let startIndex = 0;

    const onTouchStart = (e: TouchEvent) => {
      isScrollingProgrammaticallyRef.current = false;
      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      directionLockedRef.current = null;
      startIndex = currentIndexRef.current;

      // Proactively reset all neighbors to top
      resetNeighborScrollTops(startIndex);
    };

    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      // Lock direction after a small threshold
      if (directionLockedRef.current === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        directionLockedRef.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (directionLockedRef.current === 'h') {
        // Horizontal gesture: prevent vertical jitter by locking page scrolling
        pageRefs.current.forEach(el => {
          if (el) el.style.overflowY = 'hidden';
        });

        // Ensure snap neighbors are at top
        const scrollLeft = track.scrollLeft;
        const w = window.innerWidth;
        const nearestIdx = Math.round(scrollLeft / w);
        for (let i = Math.max(0, nearestIdx - 1); i <= Math.min(PAGE_DEFS.length - 1, nearestIdx + 1); i++) {
          if (i !== currentIndexRef.current && pageRefs.current[i]) {
            pageRefs.current[i]!.scrollTop = 0;
          }
        }
      }
    };

    const onTouchEnd = () => {
      // Restore vertical scrolling
      pageRefs.current.forEach(el => {
        if (el) el.style.overflowY = 'auto';
      });

      if (directionLockedRef.current === 'h') {
        const w = window.innerWidth;
        const currentScrollLeft = track.scrollLeft;
        const finalIdx = Math.round(currentScrollLeft / w);

        // Strict restriction: maximum ±1 page from where we started the touch
        const targetIdx = Math.max(0, Math.min(PAGE_DEFS.length - 1,
          Math.max(startIndex - 1, Math.min(startIndex + 1, finalIdx))
        ));

        if (targetIdx !== currentIndexRef.current) {
          track.scrollTo({ left: targetIdx * w, behavior: 'smooth' });
          currentIndexRef.current = targetIdx;
          setCurrentIndex(targetIdx);

          isInternalNavRef.current = true;
          navigate(buildPath(PAGE_DEFS[targetIdx].slug), { replace: true });
          setTimeout(() => { isInternalNavRef.current = false; }, 150);
        }
      }

      directionLockedRef.current = null;
    };

    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: true });
    track.addEventListener('touchend', onTouchEnd, { passive: true });
    track.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      track.removeEventListener('touchstart', onTouchStart);
      track.removeEventListener('touchmove', onTouchMove);
      track.removeEventListener('touchend', onTouchEnd);
      track.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isMainPage, navigate, resetNeighborScrollTops, buildPath]);

  const handleTabClick = useCallback((index: number) => {
    if (!isMainPage || index === currentIndexRef.current) return;

    // Reset target page scrollTop before scrolling to it
    if (pageRefs.current[index]) {
      pageRefs.current[index]!.scrollTop = 0;
    }

    // Block scroll updates during programmatic scroll
    isScrollingProgrammaticallyRef.current = true;
    window.clearTimeout(scrollProgrammaticTimerRef.current);

    currentIndexRef.current = index;
    setCurrentIndex(index);

    if (trackRef.current) {
      trackRef.current.scrollTo({ left: index * window.innerWidth, behavior: 'smooth' });
    }

    isInternalNavRef.current = true;
    navigate(buildPath(PAGE_DEFS[index].slug), { replace: true });

    // Clear flag when scroll is expected to end
    scrollProgrammaticTimerRef.current = window.setTimeout(() => {
      isScrollingProgrammaticallyRef.current = false;
      isInternalNavRef.current = false;
    }, 500);
  }, [isMainPage, navigate, buildPath]);

  const cycleLang = useCallback(() => {
    const langs: Array<'ru' | 'en' | 'uz'> = ['ru', 'en', 'uz'];
    const currentLangIdx = langs.indexOf(language as 'ru' | 'en' | 'uz');
    const nextLang = langs[(currentLangIdx + 1) % langs.length];

    const segments = location.pathname.split('/').filter(Boolean).slice(1);
    const currentLang = language as Lang;

    // 1. Home
    if (segments.length === 0) {
      navigate(`/${nextLang}`, { replace: true });
      return;
    }

    const firstSlug = segments[0];
    const pageKey = resolvePageKeyFromSlug(firstSlug, currentLang);
    const isUseful = pageKey === 'useful';

    // 2. Useful Section
    if (isUseful) {
      if (segments.length === 1) {
        navigate(getUsefulRootPath(nextLang), { replace: true });
        return;
      }

      const hubSlug = segments[1];
      const hubId = resolveHubIdFromSlug(hubSlug, currentLang) || hubSlug;

      if (segments.length === 2) {
        navigate(buildUsefulHubPath(nextLang, hubId), { replace: true });
        return;
      }

      const articleSlug = segments[2];
      const articleId = resolveArticleIdFromSlug(hubSlug, articleSlug, currentLang) || articleSlug;
      navigate(buildUsefulArticlePath(nextLang, hubId, articleId), { replace: true });
      return;
    }

    // 3. Standalone Pages
    if (pageKey) {
      navigate(getPagePath(pageKey, nextLang), { replace: true });
      return;
    }

    // 4. Default (Catalog, etc.)
    const rest = stripLangPrefix(location.pathname) || '/';
    const newPath = `/${nextLang}${rest === '/' ? '' : rest}`;
    navigate(newPath, { replace: true });
  }, [language, navigate, location.pathname]);

  // Scroll active tab into view
  useEffect(() => {
    if (tabsRef.current && isMainPage) {
      const activeTab = tabsRef.current.querySelector(`[data-index="${currentIndex}"]`);
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, isMainPage]);

  // On mount/resize, ensure track is at correct position
  useEffect(() => {
    if (!isMainPage) return;
    const syncPosition = () => {
      if (trackRef.current) {
        trackRef.current.scrollLeft = currentIndexRef.current * window.innerWidth;
      }
    };
    syncPosition();
    window.addEventListener('resize', syncPosition);
    return () => window.removeEventListener('resize', syncPosition);
  }, [isMainPage]);

  // Suppress unused ref warning
  void rafIdRef.current;

  if (!isMainPage) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Desktop: render children (Routes) as-is */}
      <div className="hidden lg:block">{children}</div>

      {/* Mobile/Tablet: horizontal swipe container */}
      <div className="lg:hidden fixed inset-0 overflow-hidden flex flex-col bg-[#13100B] overscroll-none" style={{ overscrollBehavior: 'none' }}>
        {/* Tab bar */}
        <div className="flex-shrink-0 z-50 bg-[#13100B] border-b border-white/5 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center justify-center py-3 border-b border-white/5 bg-[#13100B]">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfrUaM52DXdWd1f2WHAbNwcoVhba2%2F08__3e9c1a05.png?alt=media&token=08603184-d1c7-42c9-b08d-947d315212e8"
              alt="SARGO"
              className="h-6 w-auto object-contain"
            />
          </div>

          <div
            ref={tabsRef}
            className="flex items-center gap-2 px-4 py-3 overflow-x-auto scrollbar-hide bg-[#13100B]"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            <button
              onClick={cycleLang}
              className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[10px] font-black text-white/80 hover:bg-primary hover:text-white transition-all duration-300"
            >
              {language.toUpperCase()}
            </button>

            {PAGE_DEFS.map((page, index) => (
              <button
                key={page.slug}
                data-index={index}
                onClick={() => handleTabClick(index)}
                className={cn(
                  "flex-shrink-0 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.1em] transition-all duration-300 whitespace-nowrap",
                  currentIndex === index
                    ? "bg-primary text-white shadow-[0_0_15px_rgba(176,138,90,0.5)]"
                    : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/70"
                )}
              >
                {t(page.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Swipe track */}
        <div
          ref={trackRef}
          onScroll={handleScroll}
          className="mobile-swipe-track flex-1"
        >
          {PAGE_DEFS.map((page, index) => {
            const isActive = index === currentIndex;
            return (
              <div
                key={page.slug}
                ref={el => { pageRefs.current[index] = el; }}
                className="mobile-swipe-page"
              >
                <div className="min-h-full flex flex-col">
                  <div className="flex-1">
                    {/* All pages render in the same wrapper type — no conditional
                        Provider that would change the React tree and cause remounts.
                        The seoEnabled prop tells each page whether to call setSEO. */}
                    {React.createElement(page.component, { seoEnabled: isActive })}
                  </div>
                  <Footer className={page.slug === 'about' ? "pt-10" : ""} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
